import { useState } from 'react'
import { Switch } from '@/components/ui/switch'
import { isTauri } from '@/lib/utils/tauri'
import { setLiveCaptions } from '@/lib/utils/tauri-recording'
import { useRecordingStore } from '@/store/recording.store'
import { toast } from 'sonner'

import { useCaptionModel } from './useCaptionModel'

/**
 * Turns live captions on or off for the recording in progress (desktop).
 * The first turn-on downloads the on-device model; the engine's state comes
 * back through `recording:caption-status` (RecorderController).
 */
export function LiveCaptionsSwitch() {
  const captionsActive = useRecordingStore(s => s.captionsActive)
  const recordingState = useRecordingStore(s => s.state)
  const { model, downloadPct, ensureReady } = useCaptionModel()
  const [isPending, setIsPending] = useState(false)

  if (!isTauri() || !model?.supported) return null

  const isOn = captionsActive === true || captionsActive === 'starting'
  const handleChange = async (next: boolean) => {
    setIsPending(true)
    try {
      if (next) {
        if (!(await ensureReady())) return
        useRecordingStore.getState().setCaptionsActive('starting')
        await setLiveCaptions(true)
      } else {
        useRecordingStore.getState().setCaptionsActive(null)
        await setLiveCaptions(false)
      }
    } catch (error) {
      toast.error(
        next ? 'Could not start live captions' : 'Could not stop live captions',
        {
          description: String(
            error instanceof Error ? error.message : error,
          ).slice(0, 160),
        },
      )
    } finally {
      setIsPending(false)
    }
  }

  return (
    <span className="flex items-center gap-1.5">
      {downloadPct !== null && (
        <span className="text-[11px] tabular-nums text-muted-foreground">
          Downloading {downloadPct}%
        </span>
      )}
      <Switch
        checked={isOn}
        disabled={
          isPending ||
          downloadPct !== null ||
          captionsActive === 'starting' ||
          recordingState === 'finalizing'
        }
        onCheckedChange={checked => void handleChange(checked)}
        aria-label={isOn ? 'Turn off live captions' : 'Turn on live captions'}
        title={
          isOn
            ? 'Turn off live captions (the recording continues)'
            : model.state === 'ready'
              ? 'Turn on live captions'
              : 'Turn on live captions (one-time 142MB on-device download)'
        }
      />
    </span>
  )
}
